/**
 * This callback is displayed as a global member.
 * @callback eventCallback
 * @param {object} event Event callback
 * @param {HTMLElement} target Event target
 */

/**
 * Properties for delegating events
 * @typedef {Object} DelegateParams
 * @property {HTMLElement} element - Element to attach the event listener to
 * @property {string} event Event to listen for
 * @property {string} selector Selector to listen for events on
 * @property {eventCallback} callback Event Callback Function
 */
interface DelegateParams<
  TK extends keyof HTMLElementEventMap,
  TE extends HTMLElement
> {
  element: HTMLElement | Document | string;
  selector: string;
  callback: (event: HTMLElementEventMap[TK], target: TE) => any;
  event?: TK;
  events?: TK[];
}

/**
 * Attaches an event listener to an ancestor node to listen for events on the
 * children nodes.
 *
 * @param {DelegateParams} params Information about the event listener
 * @returns {void} A function to remove the attached event listener
 */
export const delegate = <
  TK extends keyof HTMLElementEventMap,
  TE extends HTMLElement
>(
  params: DelegateParams<TK, TE>
) => {
  const { element, selector, event, callback, events } = params;

  const internalCallback = (event: HTMLElementEventMap[TK]) => {
    const target = findTarget(event, selector) as TE;
    if (!target) return;
    callback(event, target);
  };

  let elem: TE;
  if (typeof element === 'string') {
    elem = document.querySelector<TE>(element);
  } else if ((element as Document).documentElement) {
    elem = (element as Document).documentElement as TE;
  } else {
    elem = element as TE;
  }

  // const elem = (
  //   typeof element === 'string'
  //     ? document.querySelector<TE>(element)
  //     : (element as Document).documentElement
  //     ? (element as Document).documentElement
  //     : element
  // ) as TE;
  if (!elem) return null;

  const arrEvents: TK[] = [];
  if (event) arrEvents.push(event);
  if (events) arrEvents.push(...events);

  arrEvents.map((evt) => elem.addEventListener(evt, internalCallback));

  return () => {
    arrEvents.forEach((evt) => {
      if (elem) {
        elem.removeEventListener(evt, internalCallback);
      }
    });
  };
};

/**
 *
 * @param {Event} evt - Event to find target from
 * @param {string} selector - Selector string to find target with
 */
export const findTarget = <TE extends HTMLElement | SVGElement>(
  evt: Event,
  selector: string
) => {
  if (
    !evt.target ||
    !(evt.target instanceof HTMLElement || evt.target instanceof SVGElement) ||
    typeof evt.target.matches === typeof undefined
  )
    return undefined;

  const target = evt.target.matches(selector)
    ? (evt.target as TE)
    : evt.target.closest<TE>(selector);
  return target;
};
